"use client";

import type { CSSProperties, ReactNode } from "react";
import type {
	GeneralConfig,
	LayoutConfig,
	MotionConfig,
	StyleConfig,
} from "@/components/landing/registry/types";
import { createContext, use } from "react";
import {
	decorativeClassName,
	generalSectionClassName,
	opacityStyle,
} from "@/components/landing/shared/sectionStyle";
import { cn } from "@/lib/utils";

const SectionMotionContext = createContext<MotionConfig | null>(null);

/**
 * Every section's internal `<Reveal delay={n}>` blocks call this to pick up
 * the instance's configured Motion tab settings instead of a hardcoded CSS
 * fade. Falls back to a sensible default outside a shell (e.g. Storybook-ish
 * standalone renders) so sections never crash without a provider.
 */
export function useSectionMotion(): MotionConfig {
	return (
		use(SectionMotionContext) ?? {
			type: "slideUp",
			duration: 700,
			delay: 0,
			easing: "easeOut",
			viewportTrigger: "once",
			stagger: 0,
			hover: "none",
			idle: "none",
		}
	);
}

interface SectionShellProps {
	general: GeneralConfig;
	layout: LayoutConfig;
	style: StyleConfig;
	motion: MotionConfig;
	className?: string;
	children: ReactNode;
	as?: "section" | "footer";
}

/**
 * Wraps every public landing section's outer `<section>`, applying the
 * Studio's General/Style tab values as classes/vars and exposing Motion tab
 * values to the section's own internal reveal blocks via context. `layout`
 * values are consumed directly by each section's own grid markup (columns/
 * gap/reverse/verticalAlign only make sense where that section already lays
 * out a 2-column grid) rather than here.
 */
export function SectionShell(prop: SectionShellProps) {
	const { general, layout, style, motion, className, children, as = "section" } = prop;
	void layout;
	const Tag = as;
	const styleVars: CSSProperties = { ...opacityStyle(style) };
	return (
		<SectionMotionContext value={motion}>
			<Tag
				id={general.anchorId || undefined}
				className={cn(generalSectionClassName(general), decorativeClassName(style), className)}
				style={styleVars}
			>
				{children}
			</Tag>
		</SectionMotionContext>
	);
}
